// src/pages/assets/AssetLedger.js

import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

import {
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
    generateAssetLedger,
} from "../../services/assets/assetLedgerService";

import {
    WORKFLOW_STATES,
} from "../../services/assets/workflowStates";

// ======================================================
// HELPERS
// ======================================================

const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
        if (timestamp?.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: true,
            });
        }
        return "-";
    } catch { return "-"; }
};

const STATUS_CONFIG = {
    [WORKFLOW_STATES.FINALIZED]:          { label: "Finalized",      bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
    [WORKFLOW_STATES.PENDING_ACCEPTANCE]: { label: "Pending",        bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
    [WORKFLOW_STATES.RETURN_PENDING]:     { label: "Return Pending", bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
    [WORKFLOW_STATES.REJECTED]:           { label: "Rejected",       bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
    [WORKFLOW_STATES.CANCELLED]:          { label: "Cancelled",      bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
};

const MOVEMENT_CONFIG = {
    INITIAL_STOCK: { bg: "#ecfdf5", color: "#059669" },
    TRANSFER:      { bg: "#eff6ff", color: "#1d4ed8" },
    RETURN:        { bg: "#f0fdf4", color: "#15803d" },
    ISSUE:         { bg: "#fdf4ff", color: "#9333ea" },
};

// ======================================================
// COMPONENT
// ======================================================

export default function AssetLedger() {

    const [loading,          setLoading]          = useState(false);
    const [error,            setError]            = useState("");
    const [ledger,           setLedger]           = useState(null);
    const [assetId,          setAssetId]          = useState("");
    const [assets,           setAssets]           = useState([]);
    const [users,            setUsers]            = useState([]);
    const [assetSearch,      setAssetSearch]      = useState("");
    const [assetSuggestions, setAssetSuggestions] = useState([]);

    const user       = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id = localStorage.getItem("company_id") || user?.company_id;
    const location   = useLocation();

    const resolveName = (id, fallback) => {
        if (!id) return fallback || "-";
        const found = users.find((u) => (u.uid || u.id) === id || u.id === id);
        if (found) return found.name || found.display_name || found.email?.split("@")[0] || fallback || id;
        return fallback || id;
    };

    const loadMasterData = useCallback(async () => {
        if (!company_id) return;
        try {
            const [assetSnap, userSnap] = await Promise.all([
                getDocs(query(collection(db, "asset_master"), where("company_id", "==", company_id))),
                getDocs(query(collection(db, "users"),        where("company_id", "==", company_id))),
            ]);
            setAssets(assetSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setUsers(userSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) { console.error("LOAD_MASTER_ERROR", err); }
    }, [company_id]);

    const loadLedger = async (selectedAssetId) => {
        if (!selectedAssetId) return;
        try {
            setLoading(true);
            setError("");
            const response = await generateAssetLedger(selectedAssetId, company_id);
            setLedger(response);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load asset ledger");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        loadMasterData();
        const params = new URLSearchParams(location.search);
        const urlAssetId = params.get("asset_id");
        if (urlAssetId) {
            setAssetId(urlAssetId);
            setLedger(null);
            loadLedger(urlAssetId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadMasterData, location.search]);

    const conditionUI = {
        WORKING:   { label: "Working",   bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", dot: "#10b981" },
        IN_REPAIR: { label: "In Repair", bg: "#fffbeb", color: "#d97706", border: "#fde68a", dot: "#f59e0b" },
        DAMAGED:   { label: "Damaged",   bg: "#fef2f2", color: "#dc2626", border: "#fecaca", dot: "#ef4444" },
        LOST:      { label: "Lost",      bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe", dot: "#8b5cf6" },
    };

    return (
        <div style={{ padding: "24px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "system-ui, -apple-system, sans-serif" }}>

            {/* HEADER */}
            <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{
                        width: "42px", height: "42px", borderRadius: "12px",
                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "20px", boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                    }}>📋</div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.5px" }}>
                            Asset Ledger
                        </h2>
                        <p style={{ margin: "2px 0 0", color: "#94a3b8", fontSize: "13px", fontWeight: "500" }}>
                            Replay-safe custody history
                        </p>
                    </div>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div style={{
                background: "#fff", padding: "20px", borderRadius: "16px",
                marginBottom: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)",
                display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap",
                border: "1px solid #e2e8f0",
            }}>
                <div style={{ flex: 1, minWidth: "280px", position: "relative" }}>
                    <div style={{
                        position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                        color: "#94a3b8", fontSize: "16px", pointerEvents: "none",
                    }}>🔎</div>
                    <input
                        type="text"
                        placeholder="Search asset by name, code or category..."
                        value={assetSearch}
                        onChange={(e) => {
                            setAssetSearch(e.target.value);
                            setAssetId("");
                            setLedger(null);
                            const lower = e.target.value.toLowerCase();
                            if (!lower.trim()) { setAssetSuggestions([]); return; }
                            setAssetSuggestions(
                                assets.filter(a =>
                                    (a.asset_name || "").toLowerCase().includes(lower) ||
                                    (a.asset_code || a.serial_no || "").toLowerCase().includes(lower) ||
                                    (a.asset_category || "").toLowerCase().includes(lower)
                                ).slice(0, 15)
                            );
                        }}
                        style={{
                            width: "100%", padding: "11px 14px 11px 38px",
                            border: "1.5px solid #e2e8f0", borderRadius: "10px",
                            fontSize: "14px", color: "#0f172a", background: "#f8fafc",
                            outline: "none", boxSizing: "border-box",
                            transition: "border-color 0.15s",
                        }}
                        onFocus={e => e.target.style.borderColor = "#6366f1"}
                        onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                    />
                    {assetSuggestions.length > 0 && (
                        <div style={{
                            position: "absolute", top: "100%", left: 0, right: 0,
                            background: "#fff", border: "1px solid #e2e8f0",
                            borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                            zIndex: 9999, maxHeight: "240px", overflowY: "auto", marginTop: "6px",
                        }}>
                            {assetSuggestions.map(a => (
                                <div key={a.id}
                                    onClick={() => {
                                        setAssetId(a.id);
                                        setAssetSearch(`${a.asset_name || "Unnamed"} — ${a.asset_code || a.serial_no || "No Code"}`);
                                        setAssetSuggestions([]);
                                        setLedger(null);
                                        loadLedger(a.id);
                                    }}
                                    style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: "14px" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f5f3ff"}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                >
                                    <div style={{ fontWeight: "600", color: "#0f172a" }}>{a.asset_name || "Unnamed"}</div>
                                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", display: "flex", alignItems: "center", gap: "6px" }}>
                                        <span>{a.asset_code || a.serial_no || "No Code"}</span>
                                        {a.asset_category && (
                                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "1px 8px", borderRadius: "999px", fontSize: "10px", fontWeight: "600" }}>
                                                {a.asset_category}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => loadLedger(assetId)}
                    disabled={loading || !assetId}
                    style={{
                        background: loading || !assetId
                            ? "#e2e8f0"
                            : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                        color: loading || !assetId ? "#94a3b8" : "#fff",
                        border: "none",
                        padding: "11px 22px", borderRadius: "10px",
                        cursor: loading || !assetId ? "not-allowed" : "pointer",
                        fontWeight: "700", fontSize: "14px", whiteSpace: "nowrap",
                        boxShadow: loading || !assetId ? "none" : "0 4px 12px rgba(99,102,241,0.3)",
                        transition: "all 0.15s",
                    }}
                >
                    {loading ? "Loading..." : "Load Ledger"}
                </button>
            </div>

            {/* ERROR */}
            {error && (
                <div style={{
                    background: "#fef2f2", color: "#dc2626", padding: "14px 18px",
                    borderRadius: "12px", marginBottom: "20px", fontSize: "14px",
                    border: "1px solid #fecaca", display: "flex", alignItems: "center", gap: "8px",
                }}>
                    ⚠️ {error}
                </div>
            )}

            {/* LOADING */}
            {loading && (
                <div style={{
                    background: "#fff", borderRadius: "16px", padding: "56px",
                    textAlign: "center", border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}>
                    <div style={{ fontSize: "32px", marginBottom: "12px" }}>⏳</div>
                    <div style={{ color: "#64748b", fontSize: "15px", fontWeight: "500" }}>Loading ledger...</div>
                </div>
            )}

            {/* LEDGER */}
            {ledger && !loading && (
                <>
                    {/* ASSET SUMMARY CARD */}
                    <div style={{
                        background: "#fff", borderRadius: "16px", padding: "24px",
                        marginBottom: "20px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)",
                        border: "1px solid #e2e8f0",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
                            <span style={{ fontSize: "16px" }}>🗂️</span>
                            <span style={{ fontWeight: "700", fontSize: "15px", color: "#0f172a" }}>Asset Summary</span>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
                            <SummaryCard label="Asset Name"     value={ledger.asset.asset_name}     icon="🔧" accent="#6366f1" />
                            <SummaryCard label="Category"       value={ledger.asset.asset_category} icon="📁" accent="#8b5cf6" />
                            <SummaryCard label="Ownership"      value={ledger.asset.ownership_mode} icon="🏷️" accent="#06b6d4" />
                            <SummaryCard label="Current Holder" value={
                                ledger.current_holder?.holder_name ||
                                ledger.current_holder_snapshot?.holder_name || "-"
                            } icon="👤" accent="#10b981" />
                            <SummaryCard label="Transactions"   value={ledger.total_transactions}   icon="🔄" accent="#f59e0b" />
                            <SummaryCard label="Pending"        value={ledger.pending_transactions || "0"} icon="⏳" accent="#ef4444" />

                            {/* CONDITION CARD */}
                            {(() => {
                                const c  = ledger.asset.condition || "WORKING";
                                const ui = conditionUI[c] || { label: c, bg: "#f8fafc", color: "#64748b", border: "#e2e8f0", dot: "#94a3b8" };
                                return (
                                    <div style={{
                                        background: ui.bg, padding: "16px",
                                        borderRadius: "12px", border: `1.5px solid ${ui.border}`,
                                    }}>
                                        <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
                                            Condition
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: ui.dot, display: "inline-block", flexShrink: 0 }} />
                                            <span style={{ background: "transparent", color: ui.color, fontSize: "13px", fontWeight: "700" }}>
                                                {ui.label}
                                            </span>
                                        </div>
                                        {ledger.asset.condition_updated_by_name && (
                                            <p style={{ margin: "6px 0 0", fontSize: "11px", color: "#94a3b8" }}>
                                                by {ledger.asset.condition_updated_by_name}
                                            </p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* LOST / DAMAGED BANNER */}
                        {(ledger.asset.condition === "LOST" || ledger.asset.condition === "DAMAGED") && (() => {
                            const isLost = ledger.asset.condition === "LOST";
                            return (
                                <div style={{
                                    marginTop: "20px",
                                    background: isLost ? "#faf5ff" : "#fff7ed",
                                    border: `1.5px solid ${isLost ? "#ddd6fe" : "#fed7aa"}`,
                                    borderRadius: "14px",
                                    padding: "20px 24px",
                                    display: "flex",
                                    gap: "16px",
                                    alignItems: "flex-start",
                                }}>
                                    <div style={{
                                        width: "48px", height: "48px", borderRadius: "12px",
                                        background: isLost ? "#ede9fe" : "#ffedd5",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: "22px", flexShrink: 0,
                                        border: `1px solid ${isLost ? "#ddd6fe" : "#fed7aa"}`,
                                    }}>
                                        {isLost ? "🔍" : "⚠️"}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: 0, fontWeight: "800", fontSize: "15px", color: isLost ? "#6d28d9" : "#c2410c" }}>
                                            {isLost ? "Asset Reported Lost" : "Asset Reported Damaged"}
                                        </p>
                                        <p style={{ margin: "2px 0 0", fontSize: "12px", color: isLost ? "#8b5cf6" : "#ea580c" }}>
                                            {isLost ? "This asset is under investigation" : "Damage has been reported — action required"}
                                        </p>
                                        <div style={{ marginTop: "14px", display: "flex", flexWrap: "wrap", gap: "20px" }}>
                                            {ledger.asset.condition_updated_by_name && (
                                                <div>
                                                    <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.06em" }}>Reported By</p>
                                                    <p style={{ margin: "3px 0 0", fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{ledger.asset.condition_updated_by_name}</p>
                                                </div>
                                            )}
                                            {ledger.asset.condition_updated_at && (
                                                <div>
                                                    <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.06em" }}>Reported At</p>
                                                    <p style={{ margin: "3px 0 0", fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{formatDate(ledger.asset.condition_updated_at)}</p>
                                                </div>
                                            )}
                                            {ledger.current_holder?.holder_name && (
                                                <div>
                                                    <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.06em" }}>Last Holder</p>
                                                    <p style={{ margin: "3px 0 0", fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{ledger.current_holder.holder_name}</p>
                                                </div>
                                            )}
                                            {ledger.asset.condition_notes && (
                                                <div>
                                                    <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.06em" }}>Notes</p>
                                                    <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#475569" }}>{ledger.asset.condition_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ marginTop: "14px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                            <span style={{
                                                background: isLost ? "#ede9fe" : "#ffedd5",
                                                color: isLost ? "#6d28d9" : "#c2410c",
                                                border: `1px solid ${isLost ? "#ddd6fe" : "#fed7aa"}`,
                                                padding: "5px 14px", borderRadius: "999px",
                                                fontSize: "12px", fontWeight: "700",
                                            }}>
                                                {isLost ? "🔍 Under Investigation" : "⚠️ Damage Reported"}
                                            </span>
                                            <span style={{
                                                background: "#f8fafc", color: "#64748b",
                                                border: "1px solid #e2e8f0",
                                                padding: "5px 14px", borderRadius: "999px",
                                                fontSize: "12px", fontWeight: "600",
                                            }}>
                                                Action Required by Owner / Manager
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* REPLAY TIMELINE */}
                    <div style={{
                        background: "#fff", borderRadius: "16px", padding: "24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)",
                        border: "1px solid #e2e8f0",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
                            <span style={{ fontSize: "16px" }}>📜</span>
                            <span style={{ fontWeight: "700", fontSize: "15px", color: "#0f172a" }}>Replay Timeline</span>
                            <span style={{
                                marginLeft: "auto", background: "#f1f5f9", color: "#64748b",
                                padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: "600",
                            }}>
                                {ledger.ledger_rows.length} transactions
                            </span>
                        </div>

                        {ledger.ledger_rows.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "48px", color: "#94a3b8" }}>
                                <div style={{ fontSize: "32px", marginBottom: "8px" }}>📭</div>
                                <p style={{ margin: 0, fontWeight: "500" }}>No transactions found</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                                    <thead>
                                        <tr style={{ background: "#f8fafc" }}>
                                            {["#", "Date", "Status", "Movement", "From", "To", "Result Holder", "Carried By", "Requested By", "Approved By", "Remarks"].map((h) => (
                                                <th key={h} style={{
                                                    padding: "12px 14px", textAlign: "left",
                                                    fontSize: "11px", fontWeight: "700",
                                                    color: "#64748b", textTransform: "uppercase",
                                                    letterSpacing: "0.06em", whiteSpace: "nowrap",
                                                    borderBottom: "2px solid #e2e8f0",
                                                }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...ledger.ledger_rows].reverse().map((row, idx) => {
                                            const mvConfig = MOVEMENT_CONFIG[row.movement_type] || { bg: "#f1f5f9", color: "#475569" };
                                            return (
                                                <tr key={row.transaction_id} style={{
                                                    borderBottom: "1px solid #f1f5f9",
                                                    background: idx % 2 === 0 ? "#fff" : "#fafbfc",
                                                    transition: "background 0.1s",
                                                }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#f5f3ff"}
                                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                                                >
                                                    <td style={{ padding: "12px 14px", fontSize: "12px", color: "#94a3b8", fontWeight: "700" }}>
                                                        {row.sequence}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "12px", color: "#475569", whiteSpace: "nowrap" }}>
                                                        {formatDate(row.created_at)}
                                                    </td>
                                                    <td style={{ padding: "12px 14px" }}>
                                                        <StatusBadge status={row.status} />
                                                    </td>
                                                    <td style={{ padding: "12px 14px" }}>
                                                        <span style={{
                                                            fontSize: "11px", fontWeight: "700",
                                                            color: mvConfig.color, background: mvConfig.bg,
                                                            padding: "3px 10px", borderRadius: "999px",
                                                            letterSpacing: "0.03em",
                                                        }}>
                                                            {row.movement_type || "-"}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#374151" }}>
                                                        {resolveName(row.from_id, row.from_holder_name || row.from_snapshot?.holder_name)}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#374151" }}>
                                                        {resolveName(row.to_id, row.to_holder_name || row.to_snapshot?.holder_name)}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>
                                                        {resolveName(
                                                            row.resulting_holder?.holder_id,
                                                            row.resulting_holder?.holder_name || row.resulting_holder_snapshot?.holder_name
                                                        ) || row.resulting_holder?.holder_type || "-"}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#374151" }}>
                                                        {row.carried_by_name
                                                            ? <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: "600" }}>
                                                                {row.carried_by_name}
                                                              </span>
                                                            : <span style={{ color: "#cbd5e1" }}>—</span>
                                                        }
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#374151" }}>
                                                        {resolveName(row.requested_by, row.requested_by_name)}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#374151" }}>
                                                        {resolveName(row.approved_by || row.received_by, row.approved_by_name)}
                                                    </td>
                                                    <td style={{ padding: "12px 14px", fontSize: "12px", color: "#94a3b8", fontStyle: row.remarks ? "normal" : "italic" }}>
                                                        {row.remarks || "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// ======================================================
// SUMMARY CARD
// ======================================================

function SummaryCard({ label, value, icon, accent = "#6366f1" }) {
    return (
        <div style={{
            background: "#f8fafc", padding: "16px",
            borderRadius: "12px", border: "1.5px solid #e2e8f0",
            transition: "border-color 0.15s",
        }}
            onMouseEnter={e => e.currentTarget.style.borderColor = accent}
            onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
        >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                {icon && <span style={{ fontSize: "13px" }}>{icon}</span>}
                <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {label}
                </span>
            </div>
            <div style={{ fontWeight: "700", fontSize: "15px", color: "#0f172a" }}>
                {value || "-"}
            </div>
        </div>
    );
}

// ======================================================
// STATUS BADGE
// ======================================================

function StatusBadge({ status }) {
    const config = STATUS_CONFIG[status] || { label: status || "-", bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
    return (
        <span style={{
            background: config.bg, color: config.color,
            border: `1px solid ${config.border}`,
            padding: "3px 10px", borderRadius: "999px",
            fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap",
        }}>
            {config.label}
        </span>
    );
}